import { DrawType } from './markupTools'

// Lucide glyphs (24×24) as separate subpaths, NOT joined into one `d`: a
// relative-`m` subpath would be offset by the prior subpath's end and land off
// the box. Each renders as its own <path>.
const PENCIL_PATHS = [
  'M12 20h9',
  'M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z',
]
const HIGHLIGHTER_PATHS = [
  'm9 11-6 6v3h9l3-3',
  'm22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4',
]
const ERASER_PATHS = [
  'M21 21H8a2 2 0 0 1-1.42-.587l-3.994-3.999a2 2 0 0 1 0-2.828l10-10a2 2 0 0 1 2.829 0l5.999 6a2 2 0 0 1 0 2.828L12.834 21',
  'm5.082 11.09 8.828 8.828',
]

const MIN_RADIUS = 3
const MAX_RADIUS = 40
const GLYPH = 22
const PAD = 4

function glyphFor(drawType: DrawType): string[] {
  if (drawType === 'eraser') return ERASER_PATHS
  if (drawType === 'highlighter') return HIGHLIGHTER_PATHS
  return PENCIL_PATHS
}

// A CSS `cursor` value for the Draw tool: a brush circle whose radius tracks the
// stroke/eraser thickness (the footprint, hot-spotted at the brush point), plus a
// glyph — pencil, highlighter marker, or eraser. Built as an inline SVG data-URL
// so the circle can scale with the width (a static image can't). Circle and glyph
// get a white casing so they read on any basemap.
export function drawCursor(drawType: DrawType, widthPx: number): string {
  const r = Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, widthPx / 2))
  const glyphPaths = glyphFor(drawType)
  const size = r * 2 + GLYPH
  const cx = r + PAD
  const cy = size - r - PAD
  const ring =
    `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#ffffff" stroke-width="3"/>` +
    `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#1a1d1e" stroke-width="1.5"/>`
  const s = GLYPH / 24
  // Land the glyph's tip (~3,21 in its 24-box) just up-right of the circle.
  const tx = cx + r * 0.4 - 3 * s
  const ty = cy - r * 0.4 - 21 * s
  // White casings before dark inks so the casing stays behind the ink.
  const casing = glyphPaths
    .map(
      d =>
        `<path d="${d}" fill="none" stroke="#ffffff" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>`,
    )
    .join('')
  const ink = glyphPaths
    .map(
      d =>
        `<path d="${d}" fill="none" stroke="#1a1d1e" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`,
    )
    .join('')
  const glyph = `<g transform="translate(${tx} ${ty}) scale(${s})">${casing}${ink}</g>`
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${ring}${glyph}</svg>`
  return `url("data:image/svg+xml,${encodeURIComponent(
    svg,
  )}") ${cx} ${cy}, crosshair`
}
